import React, { useEffect, useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import "./Home.css";
import logoDark from "../assets/logo-dark.png";
import aboutOffice from "../assets/revamp/about-office.webp";
import whyFeature from "../assets/revamp/why-feature.webp";
import teamCulture from "../assets/revamp/team-culture.webp";
import careerConsult from "../assets/revamp/career-consult.webp";
import careerEducation from "../assets/revamp/career-education.avif";
import careerStrategy from "../assets/revamp/career-strategy.webp";
import careerTeam from "../assets/revamp/career-team.avif";
import teamJason from "../assets/revamp/team-jason.webp";
import teamCindy from "../assets/revamp/team-cindy.webp";
import teamDancy from "../assets/revamp/team-dancy.webp";
import teamErlyna from "../assets/revamp/team-erlyna.webp";
import teamFrancis from "../assets/revamp/team-francis.webp";
import teamLeichin from "../assets/revamp/team-leichin.webp";
import teamAmy from "../assets/revamp/team-amy.webp";
import teamStephen from "../assets/revamp/team-stephen.webp";
import teamChristine from "../assets/revamp/team-christine.webp";
import teamVee from "../assets/revamp/team-vee.webp";
import teamJack from "../assets/revamp/team-jack.webp";

const YELLOW = "#F4B41A";
const NAVY = "#0A1530";
const GOLD_CTA = "linear-gradient(180deg,#ffd045,#F4B41A)";
const sans = "'Hanken Grotesk',sans-serif";
const display = "'Space Grotesk',sans-serif";

/* Renders a real slot image, filling its (already styled) container. */
function Img({ src, alt, fit = "cover", bg }) {
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={{ width: "100%", height: "100%", objectFit: fit, display: "block", background: bg }}
    />
  );
}

/* eyebrow label with the v2 leading dash */
function Eyebrow({ color, className, style, children }) {
  return (
    <div
      className={className}
      style={{ display: "flex", alignItems: "center", gap: "14px", fontSize: "13px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color, ...style }}
    >
      <span style={{ display: "block", width: "28px", height: "1px", background: color }} />
      {children}
    </div>
  );
}

function Chevron({ size = "15px" }) {
  return <span style={{ color: YELLOW, fontWeight: 800, fontFamily: display, fontSize: size }}>›</span>;
}

/* ---- repeated content ---- */
const NAV_LINKS = [
  ["#about", "About", false],
  ["#services", "Services", false],
  ["#why", "Why Us", false],
  ["#team", "Our Team", false],
  ["#career", "Careers", true],
  ["#contact", "Contact", false],
];

const SOCIAL_LINKS = [
  ["f", "https://www.facebook.com/xenwealthmy/"],
  ["in", "https://my.linkedin.com/company/xenwealth-group"],
  ["IG", "https://www.instagram.com/xenwealthmy/"],
];

const TICKER = [
  "Financial Planning",
  "Wealth Protection",
  "Retirement Planning",
  "Investment Advisory",
  "Estate & Legacy",
  "Business Continuity",
];

const SERVICES = [
  ["01", "Financial Planning", "Holistic plans that map your goals to a clear, actionable roadmap.", null],
  ["02", "Wealth Protection", "Insurance and risk strategies that safeguard what you've built.", ".07s"],
  ["03", "Retirement Planning", "Build income you can rely on for the life you want, later.", ".14s"],
  ["04", "Investment Advisory", "Disciplined, goal-aligned strategies matched to your risk profile.", null],
  ["05", "Estate & Legacy", "Pass on wealth smoothly and protect the people who matter most.", ".07s"],
  ["06", "Business Continuity", "Keep your business resilient with key-person and succession planning.", ".14s"],
];

const WHY = [
  ["Professional Advisors", "Certified professionals with deep industry knowledge and proven expertise.", null],
  ["Tailored Solutions", "Strategies built around your lifestyle, risk tolerance and long-term goals.", ".08s"],
  ["Trusted Approach", "Recommendations grounded in integrity and a commitment to lasting value.", ".16s"],
  ["Long-Term Partnership", "Supporting you through every stage of your financial journey.", ".24s"],
];

const PROCESS = [
  ["01", "Discover", "We listen first — your goals, concerns and full financial picture.", null],
  ["02", "Strategize", "We design a tailored plan with clear priorities and honest trade-offs.", ".08s"],
  ["03", "Implement", "We put the plan to work and handle the moving parts alongside you.", ".16s"],
  ["04", "Review", "We track progress and adapt as life and markets change.", ".24s"],
];

const FEATURE_BULLETS = [
  ["Plain-language explanations — never jargon for its own sake.", ".06s"],
  ["Transparent recommendations aligned to your interests.", ".12s"],
  ["One point of contact who genuinely knows your story.", ".18s"],
  ["Regular reviews to keep your plan on track over time.", ".24s"],
];

const TEAM = [
  ["Jason Ting", "Co-Founder", null, teamJason],
  ["Cindy Cheah", "Co-Founder", ".06s", teamCindy],
  ["Dancy Tee", "Director", ".12s", teamDancy],
  ["Erlyna", "Senior Consultant", ".18s", teamErlyna],
  ["Francis", "Senior Consultant", null, teamFrancis],
  ["Lei Chin", "Senior Consultant", ".06s", teamLeichin],
  ["Amy Chuah", "Senior Consultant", ".12s", teamAmy],
  ["Stephen Tey", "Senior Consultant", ".18s", teamStephen],
  ["Christine Lee", "Consultant", null, teamChristine],
  ["Vee Li", "Consultant", ".06s", teamVee],
  ["Jack Lee", "Consultant", ".12s", teamJack],
];

const CAREER_EXP = [
  ["01", "Client Consultations", "Engage directly with clients to understand their financial goals, challenges and life priorities — while building real-world advisory communication and relationship skills.", "left", careerConsult],
  ["02", "Financial Education", "Structured learning sessions covering planning fundamentals, wealth protection strategies and practical advisory frameworks — guided by experienced mentors.", "right", careerEducation],
  ["03", "Strategy & Planning", "See how financial strategies are crafted, reviewed and aligned with both short-term needs and long-term security goals — to deliver real, lasting value to clients.", "left", careerStrategy],
  ["04", "Team Collaboration", "Work closely with a supportive team through discussions, activities and interactive sessions that build confidence, communication, leadership and teamwork.", "right", careerTeam],
];

const CONSULTANT_LIST = [
  "Financial advisory skills",
  "Client communication confidence",
  "Personal branding",
  "Leadership pathway",
  "Long-term career freedom",
];

const INTERNSHIP_LIST = [
  "Admin and HR operations exposure",
  "Sales and marketing role learning",
  "Financial literacy training",
  "Client communication exposure",
  "Team projects and activities",
  "Personal growth and leadership habits",
];

/* Fixed 3D "scene" backdrop — starfield flying toward the viewer on scroll
   plus a wireframe extruded "›" logo that rotates with scroll, and a subtle
   rotateX/scale on each .x3s section. Ported from the design's _wire3D
   (scene mode only). Returns a cleanup fn. */
function startScene(canvas) {
  const ctx = canvas.getContext("2d");
  if (!ctx) return () => {};
  const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const DPR = Math.min(1.5, window.devicePixelRatio || 1);
  let W = 0, H = 0, t = 0, raf = 0;

  const stars = [];
  let sSeed = 97;
  const rnd = () => { sSeed = (sSeed * 16807) % 2147483647; return sSeed / 2147483647; };
  for (let i = 0; i < 80; i++) stars.push({ x: rnd() * 2 - 1, y: rnd() * 2 - 1, z: rnd(), g: rnd() < 0.7, c: rnd() < 0.22 });

  const mouse = { x: 0, y: 0, tx: 0, ty: 0 };
  const onMove = (e) => {
    mouse.tx = e.clientX / window.innerWidth - 0.5;
    mouse.ty = e.clientY / window.innerHeight - 0.5;
  };
  window.addEventListener("pointermove", onMove, { passive: true });

  const face = [[-0.5, -1], [0.5, 0], [-0.5, 1], [-0.5, 0.58], [0.06, 0], [-0.5, -0.58]];
  const EXT = 0.17;

  const resize = () => {
    W = window.innerWidth;
    H = window.innerHeight;
    if (canvas.width !== Math.round(W * DPR) || canvas.height !== Math.round(H * DPR)) {
      canvas.width = W * DPR;
      canvas.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    }
  };

  const drawScene = () => {
    resize();
    ctx.clearRect(0, 0, W, H);
    const scr = window.scrollY || 0;
    const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
    const prog = scr / max;
    ctx.globalCompositeOperation = "lighter";

    for (const s of stars) {
      const z = (((s.z - prog * 1.6 - t * 0.008) % 1) + 1) % 1;
      const f = 0.9 / (0.08 + z * 0.92);
      const sx = W / 2 + (s.x + mouse.x * 0.06) * W * 0.6 * f;
      const sy = H / 2 + (s.y + mouse.y * 0.06) * H * 0.6 * f;
      if (sx < -40 || sx > W + 40 || sy < -40 || sy > H + 40) continue;
      const a = (1 - z) * 0.5 + 0.06;
      if (s.c) {
        const sz = (1 - z) * 10 + 2;
        ctx.beginPath();
        ctx.moveTo(sx - sz * 0.45, sy - sz);
        ctx.lineTo(sx + sz * 0.45, sy);
        ctx.lineTo(sx - sz * 0.45, sy + sz);
        ctx.strokeStyle = s.g ? "rgba(244,180,26," + (a * 0.85).toFixed(3) + ")" : "rgba(140,170,255," + (a * 0.7).toFixed(3) + ")";
        ctx.lineWidth = 1.2;
        ctx.lineJoin = "round";
        ctx.lineCap = "round";
        ctx.stroke();
      } else {
        ctx.beginPath();
        ctx.arc(sx, sy, (1 - z) * 1.8 + 0.4, 0, Math.PI * 2);
        ctx.fillStyle = s.g ? "rgba(244,200,90," + a.toFixed(3) + ")" : "rgba(150,180,255," + (a * 0.8).toFixed(3) + ")";
        ctx.fill();
      }
    }

    const cx = W * 0.72, cy = H * 0.5, S = Math.min(W, H) * 0.3;
    const ry = prog * Math.PI * 2.2 + t * 0.1 + mouse.x * 0.5;
    const rx = -0.15 + mouse.y * 0.4 + Math.sin(t * 0.3) * 0.05;
    const P3 = (p, zoff) => {
      const x1 = p[0] * Math.cos(ry) + zoff * Math.sin(ry);
      const z1 = -p[0] * Math.sin(ry) + zoff * Math.cos(ry);
      const y1 = p[1] * Math.cos(rx) - z1 * Math.sin(rx);
      const z2 = p[1] * Math.sin(rx) + z1 * Math.cos(rx);
      const f = 2.6 / (2.6 + z2);
      return [cx + x1 * S * f, cy + y1 * S * f];
    };
    const front = face.map((p) => P3(p, EXT));
    const back = face.map((p) => P3(p, -EXT));
    const path = (pts) => { ctx.beginPath(); pts.forEach((p, i) => (i ? ctx.lineTo(p[0], p[1]) : ctx.moveTo(p[0], p[1]))); ctx.closePath(); };
    ctx.lineJoin = "round";
    path(back); ctx.strokeStyle = "rgba(140,170,255,0.07)"; ctx.lineWidth = 6; ctx.stroke();
    path(back); ctx.strokeStyle = "rgba(140,170,255,0.28)"; ctx.lineWidth = 1.4; ctx.stroke();
    ctx.beginPath();
    for (let i = 0; i < face.length; i++) { ctx.moveTo(front[i][0], front[i][1]); ctx.lineTo(back[i][0], back[i][1]); }
    ctx.strokeStyle = "rgba(244,180,26,0.2)"; ctx.lineWidth = 1.2; ctx.stroke();
    path(front); ctx.strokeStyle = "rgba(244,180,26,0.10)"; ctx.lineWidth = 10; ctx.stroke();
    path(front); ctx.strokeStyle = "rgba(244,180,26,0.30)"; ctx.lineWidth = 4.5; ctx.stroke();
    path(front); ctx.strokeStyle = "rgba(255,214,110,0.85)"; ctx.lineWidth = 2; ctx.stroke();
    front.forEach((p) => { ctx.beginPath(); ctx.arc(p[0], p[1], 2.2, 0, Math.PI * 2); ctx.fillStyle = "rgba(255,220,120,0.8)"; ctx.fill(); });
    ctx.globalCompositeOperation = "source-over";
  };

  const applySections = () => {
    const vh = window.innerHeight;
    document.querySelectorAll(".x3s").forEach((el) => {
      const r = el.getBoundingClientRect();
      if (r.bottom < -300 || r.top > vh + 300) { el.style.transform = ""; return; }
      const enter = clamp((r.top - vh * 0.55) / (vh * 0.6), 0, 1);
      const leave = clamp((vh * 0.3 - r.bottom) / (vh * 0.6), 0, 1);
      const s = enter - leave;
      el.style.transform = "perspective(1900px) rotateX(" + (s * 3.4).toFixed(2) + "deg) scale(" + (1 - Math.abs(s) * 0.02).toFixed(4) + ")";
    });
  };

  let lastScroll = -1, lastVh = 0;
  const loop = () => {
    if (!canvas.isConnected) return;
    mouse.x += (mouse.tx - mouse.x) * 0.05;
    mouse.y += (mouse.ty - mouse.y) * 0.05;
    if (!reduce) {
      t += 0.016;
      const scr = window.scrollY || 0;
      if (scr !== lastScroll || window.innerHeight !== lastVh) {
        lastScroll = scr;
        lastVh = window.innerHeight;
        applySections();
      }
      drawScene();
    }
    raf = requestAnimationFrame(loop);
  };
  applySections();
  drawScene();
  if (!reduce) raf = requestAnimationFrame(loop);

  return () => {
    cancelAnimationFrame(raf);
    window.removeEventListener("pointermove", onMove);
    document.querySelectorAll(".x3s").forEach((el) => { el.style.transform = ""; });
  };
}

export default function Home() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [sent, setSent] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const form = useRef(null);
  const sceneRef = useRef(null);
  const progressRef = useRef(null);

  // reveal + count-up + spotlight + progress bar + 3D scene
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const reveals = Array.from(document.querySelectorAll(".xw-reveal"));
    const counters = Array.from(document.querySelectorAll("[data-count]"));

    const countUp = (el) => {
      const target = parseFloat(el.dataset.count);
      if (!isFinite(target)) return;
      const prefix = el.dataset.prefix || "";
      const suffix = el.dataset.suffix || "";
      const dur = 1400;
      const start = performance.now();
      const tick = (now) => {
        const t = Math.min(1, (now - start) / dur);
        const eased = 1 - Math.pow(1 - t, 3);
        el.textContent = prefix + Math.round(target * eased) + suffix;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    };

    let io;
    if (reduce || !("IntersectionObserver" in window)) {
      reveals.forEach((el) => el.classList.add("xw-in"));
      counters.forEach((el) => {
        el.textContent = (el.dataset.prefix || "") + el.dataset.count + (el.dataset.suffix || "");
      });
    } else {
      io = new IntersectionObserver(
        (entries, obs) => {
          entries.forEach((e) => {
            if (!e.isIntersecting) return;
            e.target.classList.add("xw-in");
            if (e.target.hasAttribute("data-count")) countUp(e.target);
            obs.unobserve(e.target);
          });
        },
        { threshold: 0.15, rootMargin: "0px 0px -6% 0px" }
      );
      reveals.forEach((el) => io.observe(el));
      counters.forEach((el) => {
        if (!el.classList.contains("xw-reveal")) io.observe(el);
      });
    }

    // cursor-glow spotlight on .xw-card
    const onSpot = (e) => {
      const card = e.target && e.target.closest ? e.target.closest(".xw-card") : null;
      if (!card) return;
      const r = card.getBoundingClientRect();
      card.style.setProperty("--mx", ((e.clientX - r.left) / r.width) * 100 + "%");
      card.style.setProperty("--my", ((e.clientY - r.top) / r.height) * 100 + "%");
    };
    document.addEventListener("pointermove", onSpot, { passive: true });

    // scroll progress bar
    const onScroll = () => {
      const el = progressRef.current;
      if (!el) return;
      const h = document.documentElement;
      const max = h.scrollHeight - h.clientHeight;
      el.style.width = (max > 0 ? (h.scrollTop / max) * 100 : 0) + "%";
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    // fixed 3D scene backdrop
    let stopScene;
    if (sceneRef.current) stopScene = startScene(sceneRef.current);

    return () => {
      if (io) io.disconnect();
      document.removeEventListener("pointermove", onSpot);
      window.removeEventListener("scroll", onScroll);
      if (stopScene) stopScene();
    };
  }, []);

  const submit = (e) => {
    e.preventDefault();
    emailjs
      .sendForm("service_4e5xvce", "template_zq81f8v", form.current, "qwI7DbssnYW9mpzrV")
      .then(() => setSent(true))
      .catch((err) => {
        console.error(err);
        setSent(true);
      });
  };

  const closeMenu = () => setMenuOpen(false);

  const toggleTeam = () => {
    const opening = !teamOpen;
    if (opening) {
      document.querySelectorAll(".xw-team-extra").forEach((el) => el.classList.add("xw-in"));
    }
    setTeamOpen(opening);
  };

  const labelStyle = { display: "block", fontSize: "12px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,255,255,0.55)", marginBottom: "10px" };

  return (
    <div id="xw-root" data-x3mode="scene" style={{ width: "100%", background: NAVY, color: "#fff", fontFamily: sans }}>
      {/* fixed 3D scene backdrop */}
      <canvas ref={sceneRef} className="xw3-scene" style={{ position: "fixed", inset: 0, width: "100%", height: "100%", zIndex: 0, pointerEvents: "none", display: "none" }} />

      {/* sticky header */}
      <div id="xw-header" style={{ position: "sticky", top: 0, zIndex: 50, background: "rgba(10,21,48,0.86)", backdropFilter: "blur(14px) saturate(1.3)", WebkitBackdropFilter: "blur(14px) saturate(1.3)", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 56px", maxWidth: "1440px", margin: "0 auto" }}>
          <a href="#top" style={{ display: "block" }}>
            <img src={logoDark} alt="Xenwealth" style={{ height: "26px", width: "auto", display: "block" }} />
          </a>
          <div className="xw-nav" style={{ display: "flex", alignItems: "center", gap: "32px" }}>
            {NAV_LINKS.map(([href, label, accent]) => (
              <a key={href} href={href} style={{ fontSize: "13.5px", fontWeight: accent ? 700 : 600, letterSpacing: "0.01em", color: accent ? YELLOW : "rgba(255,255,255,0.72)" }}>{label}</a>
            ))}
          </div>
          <a href="#contact" className="xw-cta xw-cta-header" style={{ fontSize: "13px", fontWeight: 700, color: NAVY, background: YELLOW, textDecoration: "none", padding: "11px 24px", borderRadius: "100px" }}>Book Consultation</a>
          <button className="xw-burger" aria-label="Open menu" onClick={() => setMenuOpen((o) => !o)} style={{ flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", width: "42px", height: "42px", padding: 0, background: "transparent", border: 0, cursor: "pointer" }}>
            {[0, 1, 2].map((i) => (
              <span key={i} style={{ display: "block", width: "24px", height: "2px", background: "#fff", borderRadius: "2px" }} />
            ))}
          </button>
        </div>
        <div ref={progressRef} id="xw-progress" style={{ height: "2px", width: "0%", background: "linear-gradient(90deg,#F4B41A,#ffd968)", boxShadow: "0 0 12px rgba(244,180,26,0.7)" }} />
        <div className={`xw-mobile-menu${menuOpen ? " open" : ""}`} style={{ flexDirection: "column", padding: "6px 18px 18px", background: "rgba(10,21,48,0.98)", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          {NAV_LINKS.map(([href, label, accent]) => (
            <a key={href} href={href} onClick={closeMenu} style={{ fontSize: "16px", fontWeight: 600, color: accent ? YELLOW : "rgba(255,255,255,0.82)", padding: "14px 4px", borderBottom: "1px solid rgba(255,255,255,0.07)" }}>{label}</a>
          ))}
          <a href="#contact" className="xw-cta" onClick={closeMenu} style={{ textAlign: "center", fontSize: "15px", fontWeight: 700, color: NAVY, background: YELLOW, textDecoration: "none", padding: "15px 24px", borderRadius: "100px", marginTop: "14px" }}>Book Consultation</a>
        </div>
      </div>

      {/* hero — content floats over the fixed 3D scene */}
      <div id="top" className="xw-hero" style={{ padding: "92px 56px 118px", position: "relative", overflow: "hidden", background: "radial-gradient(1200px 700px at 78% -10%, #10224e 0%, #0A1530 55%)" }}>
        <div style={{ position: "absolute", inset: 0, zIndex: 1, pointerEvents: "none", background: "linear-gradient(90deg, rgba(10,21,48,0.88) 0%, rgba(10,21,48,0.45) 34%, rgba(10,21,48,0) 62%)" }} />
        <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "140px", zIndex: 1, pointerEvents: "none", background: "linear-gradient(180deg, rgba(10,21,48,0), #0A1530)" }} />
        <div style={{ position: "relative", zIndex: 2, maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ maxWidth: "960px" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "10px", padding: "8px 18px", border: "1px solid rgba(244,180,26,0.4)", background: "rgba(244,180,26,0.06)", borderRadius: "100px", marginBottom: "38px", opacity: 0, animation: "xwRise .8s cubic-bezier(.2,.7,.2,1) .05s forwards" }}>
              <span style={{ width: "7px", height: "7px", borderRadius: "50%", background: YELLOW, boxShadow: "0 0 10px rgba(244,180,26,0.9)", display: "block" }} />
              <span style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.18em", textTransform: "uppercase", color: YELLOW }}>Financial Advisory · Malaysia</span>
            </div>
            <h1 style={{ fontFamily: display, fontWeight: 700, fontSize: "98px", lineHeight: 0.96, letterSpacing: "-0.035em", margin: "0 0 32px" }}>
              <span className="xw-w" style={{ "--d": ".1s" }}><span>Empowering</span></span>
              <br />
              <span className="xw-w" style={{ "--d": ".22s" }}><span>your</span></span>{" "}
              <span className="xw-w" style={{ "--d": ".3s" }}><span>financial</span></span>
              <br />
              <span className="xw-w" style={{ "--d": ".42s" }}>
                <span className="xw-rotor">
                  <span className="xw-rotor-track">
                    <span>future.</span>
                    <span>freedom.</span>
                    <span>legacy.</span>
                    <span>growth.</span>
                    <span>future.</span>
                  </span>
                </span>
              </span>
            </h1>
            <p style={{ fontSize: "19px", lineHeight: 1.6, color: "rgba(255,255,255,0.72)", maxWidth: "520px", margin: "0 0 42px", opacity: 0, animation: "xwRise .85s cubic-bezier(.2,.7,.2,1) .55s forwards" }}>Trusted, tailored advisory for individuals, families and businesses — built on clarity, integrity and long-term partnership.</p>
            <div style={{ display: "flex", alignItems: "center", gap: "16px", opacity: 0, animation: "xwRise .85s cubic-bezier(.2,.7,.2,1) .7s forwards" }}>
              <a href="#contact" className="xw-cta" style={{ fontSize: "15px", fontWeight: 700, color: NAVY, background: GOLD_CTA, textDecoration: "none", padding: "17px 34px", borderRadius: "100px", boxShadow: "0 8px 24px rgba(244,180,26,0.25)" }}>Book a Consultation</a>
              <a href="#career" className="xw-ghost" style={{ fontSize: "15px", fontWeight: 700, color: "#fff", textDecoration: "none", padding: "16px 30px", border: "1.5px solid rgba(255,255,255,0.28)", borderRadius: "100px" }}>Join Our Team →</a>
            </div>
          </div>
        </div>
      </div>

      {/* ticker */}
      <div className="x3s xw-ticker" style={{ overflow: "hidden", borderTop: "1px solid rgba(255,255,255,0.07)", borderBottom: "1px solid rgba(255,255,255,0.07)", background: NAVY, padding: "17px 0", position: "relative", zIndex: 2 }}>
        <div className="xw-amb" style={{ display: "flex", alignItems: "center", gap: "52px", width: "max-content", animation: "xwTicker 36s linear infinite" }}>
          {[...TICKER, ...TICKER].map((word, i) => (
            <React.Fragment key={i}>
              <span style={{ fontFamily: display, fontSize: "13px", fontWeight: 600, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(255,255,255,0.34)", whiteSpace: "nowrap" }}>{word}</span>
              <span style={{ color: YELLOW, fontFamily: display, fontWeight: 700 }}>›</span>
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* about */}
      <div id="about" className="x3s" style={{ scrollMarginTop: "72px", padding: "112px 56px", background: "#0B1A3A" }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "80px", alignItems: "center", marginBottom: "104px" }}>
            <div>
              <Eyebrow className="xw-reveal" color={YELLOW} style={{ marginBottom: "20px" }}>About Xenwealth</Eyebrow>
              <h2 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "48px", lineHeight: 1.06, letterSpacing: "-0.025em", margin: "0 0 24px" }}>Sound decisions begin with clarity and trust.</h2>
              <p className="xw-reveal" style={{ "--d": ".12s", fontSize: "16px", lineHeight: 1.75, color: "rgba(255,255,255,0.72)", margin: "0 0 18px" }}>Xenwealth Group is a Malaysian-based financial consultancy helping individuals, families and businesses build sustainable financial futures through comprehensive planning and wealth protection.</p>
              <p className="xw-reveal" style={{ "--d": ".18s", fontSize: "16px", lineHeight: 1.75, color: "rgba(255,255,255,0.72)", margin: "0 0 32px" }}>Our approach is practical, transparent and goal-driven. We grow as a team, and we build for the long term.</p>
              <div className="xw-reveal" style={{ "--d": ".24s", display: "flex", gap: "14px", flexWrap: "wrap" }}>
                {["Long-term stability", "Confident decisions", "Peace of mind"].map((t) => (
                  <div key={t} style={{ borderLeft: `2px solid ${YELLOW}`, paddingLeft: "16px" }}>
                    <div style={{ fontSize: "15px", fontWeight: 700 }}>{t}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="xw-reveal img" style={{ "--d": ".12s", position: "relative" }}>
              <div style={{ position: "relative", width: "100%", aspectRatio: "4/5", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 34px 80px rgba(0,0,0,0.45)" }}>
                <Img src={aboutOffice} alt="Xenwealth office in Damansara Intan" />
              </div>
              <div style={{ position: "absolute", left: "-22px", bottom: "30px", background: GOLD_CTA, color: NAVY, borderRadius: "14px", padding: "18px 24px", boxShadow: "0 20px 44px rgba(0,0,0,0.45)" }}>
                <div style={{ fontFamily: display, fontSize: "30px", fontWeight: 700, lineHeight: 1 }}>Damansara</div>
                <div style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", opacity: 0.7, marginTop: "6px" }}>Intan · Selangor</div>
              </div>
            </div>
          </div>

          {/* stats */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px", marginBottom: "104px" }}>
            <div className="xw-reveal xw-card" style={{ background: "rgba(244,180,26,0.07)", border: "1px solid rgba(244,180,26,0.22)", borderRadius: "12px", padding: "34px 28px" }}>
              <div style={{ fontFamily: display, fontSize: "52px", fontWeight: 700, color: YELLOW, lineHeight: 1 }}>A–Z</div>
              <div style={{ fontSize: "15px", color: "rgba(255,255,255,0.62)", marginTop: "12px" }}>Every part of your financial life, covered</div>
            </div>
            <div className="xw-reveal xw-card" style={{ "--d": ".08s", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "34px 28px" }}>
              <div data-count="100" data-suffix="%" style={{ fontFamily: display, fontSize: "52px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>0%</div>
              <div style={{ fontSize: "15px", color: "rgba(255,255,255,0.62)", marginTop: "12px" }}>Strategies tailored to your goals</div>
            </div>
            <div className="xw-reveal xw-card" style={{ "--d": ".16s", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "34px 28px" }}>
              <div style={{ fontFamily: display, fontSize: "52px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>KL</div>
              <div style={{ fontSize: "15px", color: "rgba(255,255,255,0.62)", marginTop: "12px" }}>Based in Damansara Intan, Selangor</div>
            </div>
            <div className="xw-reveal xw-card" style={{ "--d": ".24s", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "34px 28px" }}>
              <div style={{ fontFamily: display, fontSize: "52px", fontWeight: 700, color: "#fff", lineHeight: 1 }}>5★</div>
              <div style={{ fontSize: "15px", color: "rgba(255,255,255,0.62)", marginTop: "12px" }}>Long-term client partnerships</div>
            </div>
          </div>

          {/* mission / vision / values */}
          <div>
            <Eyebrow className="xw-reveal" color={YELLOW} style={{ marginBottom: "20px" }}>What drives us</Eyebrow>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
              {[
                ["Mission", "Help every client make confident financial decisions through clear, honest advice.", null],
                ["Vision", "To be Malaysia's most trusted partner for long-term financial wellbeing.", ".08s"],
                ["Values", "Integrity, clarity and partnership guide every recommendation we make.", ".16s"],
              ].map(([label, text, d]) => (
                <div key={label} className="xw-reveal xw-card" style={{ ...(d ? { "--d": d } : {}), background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "36px 32px" }}>
                  <div style={{ fontFamily: display, fontSize: "13px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: YELLOW, marginBottom: "16px" }}>{label}</div>
                  <p style={{ fontSize: "17px", lineHeight: 1.6, color: "rgba(255,255,255,0.82)", margin: 0 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* services (light) */}
      <div id="services" className="x3s" style={{ scrollMarginTop: "72px", padding: "112px 56px", background: "#F5F6F9", color: "#0B1A3A" }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ maxWidth: "660px", margin: "0 0 56px" }}>
            <Eyebrow className="xw-reveal" color="#9a7a16" style={{ marginBottom: "18px" }}>What we do</Eyebrow>
            <h2 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "50px", lineHeight: 1.04, letterSpacing: "-0.025em", margin: "0 0 18px" }}>Comprehensive advisory, end to end.</h2>
            <p className="xw-reveal" style={{ "--d": ".12s", fontSize: "17px", lineHeight: 1.7, color: "rgba(11,26,58,0.66)", margin: 0 }}>From your first plan to your lasting legacy — every part of your financial life under one roof.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "20px" }}>
            {SERVICES.map(([num, title, text, d]) => (
              <div key={num} className="xw-reveal xw-card-l" style={{ ...(d ? { "--d": d } : {}), background: "#fff", border: "1px solid rgba(10,26,58,0.08)", borderRadius: "14px", padding: "36px 32px" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "22px" }}>
                  <div className="xw-num" style={{ fontFamily: display, fontSize: "34px", fontWeight: 700, color: YELLOW, lineHeight: 1 }}>{num}</div>
                  <span className="xw-go" style={{ fontFamily: display, fontSize: "20px", fontWeight: 700, color: "#9a7a16" }}>→</span>
                </div>
                <h3 style={{ fontFamily: display, fontSize: "21px", fontWeight: 700, margin: "0 0 12px" }}>{title}</h3>
                <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(11,26,58,0.62)", margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* why choose */}
      <div id="why" className="x3s" style={{ scrollMarginTop: "72px", padding: "112px 56px", background: NAVY }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "54px", gap: "40px" }}>
            <h2 className="xw-reveal" style={{ fontFamily: display, fontWeight: 700, fontSize: "50px", lineHeight: 1.04, letterSpacing: "-0.025em", margin: 0, maxWidth: "560px" }}>Why choose <span style={{ color: YELLOW }}>Xenwealth</span></h2>
            <p className="xw-reveal" style={{ "--d": ".08s", fontSize: "15px", lineHeight: 1.6, color: "rgba(255,255,255,0.6)", maxWidth: "300px", margin: 0 }}>Four reasons families and businesses trust us with what matters most.</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px", marginBottom: "108px" }}>
            {WHY.map(([title, text, d]) => (
              <div key={title} className="xw-reveal xw-card" style={{ ...(d ? { "--d": d } : {}), background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "10px", padding: "32px 26px" }}>
                <div style={{ fontFamily: display, fontSize: "40px", fontWeight: 700, color: YELLOW, lineHeight: 1, marginBottom: "24px" }}>›</div>
                <h3 style={{ fontSize: "18px", fontWeight: 700, margin: "0 0 12px" }}>{title}</h3>
                <p style={{ fontSize: "15px", lineHeight: 1.65, color: "rgba(255,255,255,0.6)", margin: 0 }}>{text}</p>
              </div>
            ))}
          </div>

          {/* process */}
          <div style={{ marginBottom: "108px" }}>
            <Eyebrow className="xw-reveal" color={YELLOW} style={{ marginBottom: "18px" }}>How we work</Eyebrow>
            <h3 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "38px", lineHeight: 1.06, letterSpacing: "-0.02em", margin: "0 0 48px", maxWidth: "540px" }}>A clear path to clarity.</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "20px" }}>
              {PROCESS.map(([num, title, text, d]) => (
                <div key={num} className="xw-reveal xw-step" style={{ ...(d ? { "--d": d } : {}), border: "1px solid rgba(255,255,255,0.1)", borderRadius: "12px", padding: "30px 26px" }}>
                  <div style={{ fontFamily: display, fontSize: "14px", fontWeight: 700, color: "rgba(255,255,255,0.35)", marginBottom: "18px" }}>{num}</div>
                  <h4 style={{ fontFamily: display, fontSize: "20px", fontWeight: 700, margin: "0 0 10px" }}>{title}</h4>
                  <p style={{ fontSize: "15px", lineHeight: 1.65, color: "rgba(255,255,255,0.62)", margin: 0 }}>{text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* feature highlight */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "72px", alignItems: "center" }}>
            <div className="xw-reveal img" style={{ position: "relative", width: "100%", aspectRatio: "4/3", borderRadius: "16px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", boxShadow: "0 30px 70px rgba(0,0,0,0.42)" }}>
              <Img src={whyFeature} alt="Advisory meeting" />
            </div>
            <div>
              <h3 className="xw-reveal" style={{ fontFamily: display, fontWeight: 700, fontSize: "36px", lineHeight: 1.08, letterSpacing: "-0.02em", margin: "0 0 28px" }}>Advice you can actually understand.</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                {FEATURE_BULLETS.map(([text, d]) => (
                  <div key={text} className="xw-reveal" style={{ "--d": d, display: "flex", gap: "14px", alignItems: "flex-start" }}>
                    <Chevron size="18px" />
                    <span style={{ fontSize: "16px", lineHeight: 1.55, color: "rgba(255,255,255,0.8)" }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* team (light) */}
      <div id="team" className="x3s" style={{ scrollMarginTop: "72px", padding: "112px 56px", background: "#F5F6F9", color: "#0B1A3A" }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: "24px", gap: "40px" }}>
            <div>
              <Eyebrow className="xw-reveal" color="#9a7a16" style={{ marginBottom: "16px" }}>Our Team</Eyebrow>
              <h2 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "50px", lineHeight: 1.04, letterSpacing: "-0.025em", margin: 0 }}>The people behind your plan.</h2>
            </div>
            <a href="#career" className="xw-cta" style={{ fontSize: "14px", fontWeight: 700, color: NAVY, textDecoration: "none", background: GOLD_CTA, padding: "13px 26px", borderRadius: "100px", whiteSpace: "nowrap" }}>Join them →</a>
          </div>
          <p className="xw-reveal" style={{ "--d": ".1s", fontSize: "17px", lineHeight: 1.7, color: "rgba(11,26,58,0.66)", maxWidth: "680px", margin: "0 0 52px" }}>A multidisciplinary team of advisors and consultants — each focused on a different part of your financial life, all working toward the same goal: yours.</p>

          {/* first row — always visible */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "32px 20px", marginBottom: 0 }}>
            {TEAM.slice(0, 4).map(([name, role, d, photo]) => (
              <div key={name} className="xw-member xw-reveal" style={d ? { "--d": d } : undefined}>
                <div style={{ position: "relative", width: "100%", aspectRatio: "3/4", borderRadius: "6px", overflow: "hidden", background: "#fff" }}>
                  <Img src={photo} alt={name} fit="contain" bg="#fff" />
                </div>
                <div style={{ fontSize: "16px", fontWeight: 700, marginTop: "14px" }}>{name}</div>
                <div style={{ fontSize: "13px", color: "#9a7a16", fontWeight: 600 }}>{role}</div>
              </div>
            ))}
          </div>

          {/* collapsible remainder */}
          <div className="xw-team-more" style={{ position: "relative", overflow: "hidden", marginTop: "32px", maxHeight: teamOpen ? "none" : "150px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "32px 20px" }}>
              {TEAM.slice(4).map(([name, role, , photo]) => (
                <div key={name} className="xw-member xw-reveal xw-team-extra">
                  <div style={{ position: "relative", width: "100%", aspectRatio: "3/4", borderRadius: "6px", overflow: "hidden", background: "#fff" }}>
                    <Img src={photo} alt={name} fit="contain" bg="#fff" />
                  </div>
                  <div style={{ fontSize: "16px", fontWeight: 700, marginTop: "14px" }}>{name}</div>
                  <div style={{ fontSize: "13px", color: "#9a7a16", fontWeight: 600 }}>{role}</div>
                </div>
              ))}
            </div>
            <div className="xw-team-fade" style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: "150px", background: "linear-gradient(to bottom, rgba(245,246,249,0), #F5F6F9)", pointerEvents: "none", transition: "opacity .4s ease", opacity: teamOpen ? 0 : 1 }} />
          </div>

          {/* toggle */}
          <div className="xw-reveal" style={{ display: "flex", justifyContent: "center", margin: "28px 0 96px" }}>
            <button onClick={toggleTeam} className="xw-ghost" style={{ fontFamily: "inherit", fontSize: "14px", fontWeight: 700, color: "#0B1A3A", background: "transparent", border: "1.5px solid rgba(10,26,58,0.22)", padding: "14px 30px", borderRadius: "100px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: "9px" }}>
              {teamOpen ? "Show less" : "Show more"}{" "}
              <span style={{ fontFamily: display, fontWeight: 800 }}>{teamOpen ? "−" : "+"}</span>
            </button>
          </div>

          {/* culture strip */}
          <div style={{ display: "grid", gridTemplateColumns: "1.15fr 0.85fr", gap: "56px", alignItems: "center", background: "#fff", border: "1px solid rgba(10,26,58,0.08)", borderRadius: "16px", padding: "18px", boxShadow: "0 20px 50px rgba(10,26,58,0.08)" }}>
            <div className="xw-reveal img" style={{ position: "relative", width: "100%", aspectRatio: "16/10", borderRadius: "10px", overflow: "hidden" }}>
              <Img src={teamCulture} alt="The Xenwealth team" />
            </div>
            <div style={{ padding: "20px 36px 20px 0" }}>
              <Eyebrow className="xw-reveal" color="#9a7a16" style={{ marginBottom: "16px" }}>Our culture</Eyebrow>
              <h3 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "30px", lineHeight: 1.1, letterSpacing: "-0.01em", margin: "0 0 16px" }}>We grow as a team.</h3>
              <p className="xw-reveal" style={{ "--d": ".12s", fontSize: "16px", lineHeight: 1.7, color: "rgba(11,26,58,0.66)", margin: 0 }}>Open mentorship, shared wins and genuine care for one another — it's how we do our best work for clients, and how new advisors find their feet fast.</p>
            </div>
          </div>
        </div>
      </div>

      {/* careers — gold centerpiece */}
      <div id="career" className="x3s" style={{ scrollMarginTop: "72px", padding: "104px 56px 96px", background: "linear-gradient(135deg,#ffcb3d 0%,#F4B41A 60%,#e6a70f 100%)", color: NAVY, position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", left: "-80px", bottom: "-200px", fontFamily: display, fontSize: "460px", lineHeight: 0.8, color: "rgba(10,21,48,0.08)", fontWeight: 700 }}>›</div>
        <div style={{ position: "relative", maxWidth: "1440px", margin: "0 auto" }}>
          <div className="xw-reveal" style={{ maxWidth: "720px" }}>
            <div style={{ fontSize: "13px", fontWeight: 700, letterSpacing: "0.2em", textTransform: "uppercase", color: "rgba(10,21,48,0.55)", marginBottom: "18px" }}>Careers at Xenwealth</div>
            <h2 style={{ fontFamily: display, fontWeight: 700, fontSize: "62px", lineHeight: 0.98, letterSpacing: "-0.028em", margin: "0 0 22px" }}>Where you'll learn, grow, and build.</h2>
            <p style={{ fontSize: "19px", lineHeight: 1.6, color: "rgba(10,21,48,0.8)", margin: 0 }}>Whether you're exploring your first step through the XFA Internship Program or building a full-time advisory career, Xenwealth is a place to grow with structure, mentorship, and real-world exposure.</p>
          </div>
        </div>
      </div>

      <div className="x3s xw-cdark" style={{ padding: "96px 56px", background: NAVY }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ marginBottom: "84px" }}>
            <div className="xw-reveal" style={{ maxWidth: "640px", margin: "0 0 60px" }}>
              <Eyebrow color={YELLOW} style={{ marginBottom: "16px" }}>Inside Xenwealth</Eyebrow>
              <h3 style={{ fontFamily: display, fontWeight: 700, fontSize: "36px", lineHeight: 1.06, letterSpacing: "-0.02em", margin: 0 }}>What you'll experience inside.</h3>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "72px" }}>
              {CAREER_EXP.map(([num, title, text, side, media]) => {
                const reel = (
                  <div className="xw-reel" style={{ ...(side === "right" ? { order: 2, justifySelf: "end" } : {}), position: "relative", width: "340px", aspectRatio: "9/16", borderRadius: "18px", overflow: "hidden", border: "1px solid rgba(255,255,255,0.12)", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
                    <Img src={media} alt={title} />
                  </div>
                );
                const copy = (
                  <div style={side === "right" ? { order: 1 } : undefined}>
                    <div style={{ fontFamily: display, fontSize: "15px", fontWeight: 700, color: "rgba(255,255,255,0.35)", marginBottom: "14px" }}>{num}</div>
                    <h4 style={{ fontFamily: display, fontWeight: 700, fontSize: "30px", letterSpacing: "-0.01em", margin: "0 0 16px" }}>{title}</h4>
                    <p style={{ fontSize: "17px", lineHeight: 1.7, color: "rgba(255,255,255,0.68)", maxWidth: "480px", margin: 0 }}>{text}</p>
                  </div>
                );
                return (
                  <div key={num} className="xw-reveal" style={{ display: "grid", gridTemplateColumns: side === "right" ? "1fr 340px" : "340px 1fr", gap: "72px", alignItems: "center" }}>
                    {side === "right" ? (<>{copy}{reel}</>) : (<>{reel}{copy}</>)}
                  </div>
                );
              })}
            </div>
          </div>

          {/* two pathways */}
          <div className="xw-reveal" style={{ marginBottom: "40px" }}>
            <Eyebrow color={YELLOW} style={{ marginBottom: "16px" }}>Two ways to join</Eyebrow>
            <h3 style={{ fontFamily: display, fontWeight: 700, fontSize: "36px", lineHeight: 1.06, letterSpacing: "-0.02em", margin: 0, maxWidth: "560px" }}>Choose your pathway.</h3>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", alignItems: "stretch" }}>
            <div className="xw-reveal xw-card" style={{ display: "flex", flexDirection: "column", background: "linear-gradient(135deg,#0d1d42,#0B1A3A)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "16px", padding: "44px 40px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)", marginBottom: "18px" }}>Career Track</div>
              <h4 style={{ fontFamily: display, fontWeight: 700, fontSize: "27px", letterSpacing: "-0.01em", margin: "0 0 14px" }}>Full-Time Consultant Career</h4>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: "0 0 26px" }}>For those who are ready to build a meaningful advisory career — helping people plan better, protect better, and make clearer financial decisions.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "13px", marginBottom: "34px" }}>
                {CONSULTANT_LIST.map((t) => (
                  <div key={t} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <Chevron />
                    <span style={{ fontSize: "15px", lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}>{t}</span>
                  </div>
                ))}
              </div>
              <a href="#contact" className="xw-cta" style={{ marginTop: "auto", alignSelf: "flex-start", fontSize: "15px", fontWeight: 700, color: NAVY, background: GOLD_CTA, textDecoration: "none", padding: "15px 30px", borderRadius: "100px" }}>Explore Consultant Career →</a>
            </div>
            <div className="xw-reveal xw-card" style={{ "--d": ".1s", display: "flex", flexDirection: "column", background: "linear-gradient(135deg,#13284f,#0d1d42)", border: "1px solid rgba(244,180,26,0.28)", borderRadius: "16px", padding: "44px 40px" }}>
              <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: YELLOW, marginBottom: "18px" }}>Learning Program</div>
              <h4 style={{ fontFamily: display, fontWeight: 700, fontSize: "27px", letterSpacing: "-0.01em", margin: "0 0 14px" }}>XFA Internship Program</h4>
              <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: "0 0 26px" }}>A practical internship for students and fresh graduates who want to learn beyond textbooks — real business exposure, team projects, financial literacy and hands-on role learning.</p>
              <div style={{ display: "flex", flexDirection: "column", gap: "13px", marginBottom: "34px" }}>
                {INTERNSHIP_LIST.map((t) => (
                  <div key={t} style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
                    <Chevron />
                    <span style={{ fontSize: "15px", lineHeight: 1.5, color: "rgba(255,255,255,0.82)" }}>{t}</span>
                  </div>
                ))}
              </div>
              <a href="#contact" className="xw-ghost" style={{ marginTop: "auto", alignSelf: "flex-start", fontSize: "15px", fontWeight: 700, color: YELLOW, background: "transparent", border: `1.5px solid ${YELLOW}`, textDecoration: "none", padding: "15px 30px", borderRadius: "100px" }}>Join XFA Internship →</a>
            </div>
          </div>

          <div className="xw-reveal" style={{ display: "flex", alignItems: "center", gap: "18px", flexWrap: "wrap", marginTop: "32px", paddingTop: "28px", borderTop: "1px solid rgba(255,255,255,0.1)" }}>
            <span style={{ fontSize: "15px", lineHeight: 1.6, color: "rgba(255,255,255,0.62)" }}>Not sure which path fits you? Reach out for a friendly chat — no formal application needed.</span>
            <a href="https://wa.me/60123456789" className="xw-cta" style={{ fontSize: "14px", fontWeight: 700, color: NAVY, background: GOLD_CTA, textDecoration: "none", padding: "12px 24px", borderRadius: "100px" }}>Chat on WhatsApp →</a>
            <span style={{ fontSize: "14px", color: "rgba(255,255,255,0.45)" }}>or xenwealthmy@gmail.com</span>
          </div>
        </div>
      </div>

      {/* contact */}
      <div id="contact" className="x3s" style={{ scrollMarginTop: "72px", padding: "112px 56px", background: "#0B1A3A", borderTop: "1px solid rgba(255,255,255,0.07)" }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ maxWidth: "640px", margin: "0 0 56px" }}>
            <Eyebrow className="xw-reveal" color={YELLOW} style={{ marginBottom: "18px" }}>Get in touch</Eyebrow>
            <h2 className="xw-reveal" style={{ "--d": ".06s", fontFamily: display, fontWeight: 700, fontSize: "50px", lineHeight: 1.04, letterSpacing: "-0.025em", margin: "0 0 16px" }}>Come say hello.</h2>
            <p className="xw-reveal" style={{ "--d": ".12s", fontSize: "17px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)", margin: 0 }}>Tell us a little about what you're looking for and we'll be in touch to arrange a friendly, no-pressure consultation.</p>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1.05fr 0.95fr", gap: "56px", alignItems: "start" }}>
            {!sent ? (
              <form ref={form} onSubmit={submit} className="xw-reveal" style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "18px" }}>
                  <div>
                    <label style={labelStyle}>Name</label>
                    <input className="xw-input" name="name" type="text" required placeholder="Your name" style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>Phone</label>
                    <input className="xw-input" name="phone" type="tel" placeholder="+60 ..." style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Email</label>
                  <input className="xw-input" name="email" type="email" required placeholder="you@email.com" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>How can we help?</label>
                  <textarea className="xw-input" name="message" rows="5" placeholder="A little about your goals or questions..." style={{ ...inputStyle, resize: "vertical" }} />
                </div>
                <button type="submit" className="xw-cta" style={{ alignSelf: "flex-start", fontSize: "15px", fontWeight: 700, color: NAVY, background: GOLD_CTA, border: 0, cursor: "pointer", padding: "16px 34px", borderRadius: "100px", fontFamily: "inherit" }}>Send message</button>
              </form>
            ) : (
              <div style={{ background: "rgba(244,180,26,0.08)", border: "1px solid rgba(244,180,26,0.3)", borderRadius: "12px", padding: "40px 36px" }}>
                <div style={{ fontFamily: display, fontSize: "26px", fontWeight: 700, color: YELLOW, marginBottom: "12px" }}>Thank you — message received.</div>
                <p style={{ fontSize: "16px", lineHeight: 1.7, color: "rgba(255,255,255,0.75)", margin: 0 }}>We'll be in touch within one business day. For anything urgent, email us at xenwealthmy@gmail.com.</p>
              </div>
            )}

            <div className="xw-reveal" style={{ "--d": ".1s" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "28px" }}>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={infoLabel}>Hours</div>
                  <div style={infoValue}>Mon – Fri, 9.00 a.m. – 6.00 p.m.<br /><span style={{ color: "rgba(255,255,255,0.5)" }}>Closed on public holidays</span></div>
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={infoLabel}>Address</div>
                  <div style={infoValue}>Suite 10-31, Level 10, Lobby 8, Block A,<br />Damansara Intan, 47400 Petaling Jaya, Selangor</div>
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={infoLabel}>Email</div>
                  <div style={infoValue}>xenwealthmy@gmail.com</div>
                </div>
              </div>
              <div style={{ borderRadius: "12px", background: "linear-gradient(150deg,#16264c,#0d1b3c)", position: "relative", overflow: "hidden", border: "1px solid rgba(255,255,255,0.1)", minHeight: "300px" }}>
                <iframe
                  title="Xenwealth Office — Damansara Intan"
                  src="https://www.google.com/maps?q=Damansara%20Intan%20Selangor&output=embed"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* footer */}
      <div className="x3s" style={{ padding: "64px 56px 40px", background: "#070F22", color: "#fff", borderTop: "1px solid rgba(255,255,255,0.08)" }}>
        <div style={{ maxWidth: "1440px", margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1.2fr", gap: "48px", paddingBottom: "44px", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
            <div>
              <img src={logoDark} alt="Xenwealth" style={{ height: "28px", width: "auto", display: "block", marginBottom: "20px" }} />
              <p style={{ fontSize: "14px", lineHeight: 1.7, color: "rgba(255,255,255,0.55)", maxWidth: "300px", margin: "0 0 20px" }}>Trusted financial advisory for individuals, families and businesses across Malaysia.</p>
              <div style={{ display: "flex", gap: "12px" }}>
                {SOCIAL_LINKS.map(([label, href]) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`Xenwealth on ${label === "in" ? "LinkedIn" : label === "IG" ? "Instagram" : "Facebook"}`}
                    style={{ width: "36px", height: "36px", border: "1px solid rgba(255,255,255,0.22)", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: label === "IG" ? "11px" : "13px", fontWeight: 700, color: "rgba(255,255,255,0.8)", textDecoration: "none" }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: YELLOW, marginBottom: "18px" }}>Quick Links</div>
              <div style={{ display: "flex", flexDirection: "column", gap: "11px" }}>
                {[["#top", "Home"], ...NAV_LINKS.slice(0, 5).map(([h, l]) => [h, l])].map(([href, label]) => (
                  <a key={href} href={href} style={{ fontSize: "14px", color: "rgba(255,255,255,0.7)", textDecoration: "none" }}>{label}</a>
                ))}
              </div>
            </div>
            <div>
              <div style={{ fontSize: "12px", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: YELLOW, marginBottom: "18px" }}>Contact</div>
              <div style={{ fontSize: "14px", lineHeight: 1.7, color: "rgba(255,255,255,0.7)" }}>xenwealthmy@gmail.com<br /><br />Suite 10-31, Level 10, Lobby 8,<br />Block A, Damansara Intan, 47400<br />Petaling Jaya, Selangor</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", paddingTop: "26px" }}>
            <div style={{ fontSize: "13px", color: "rgba(255,255,255,0.4)" }}>© 2026 Xenwealth Group — All rights reserved.</div>
            <div style={{ display: "flex", gap: "22px" }}>
              <a href="/privacy" style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Privacy Policy</a>
              <a href="/terms" style={{ fontSize: "13px", color: "rgba(255,255,255,0.55)", textDecoration: "none" }}>Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: "100%",
  background: "rgba(255,255,255,0.05)",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "10px",
  padding: "14px 16px",
  color: "#fff",
  fontSize: "15px",
  fontFamily: "inherit",
};

const infoLabel = {
  fontSize: "12px",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: YELLOW,
  width: "84px",
  flex: "none",
  paddingTop: "3px",
};

const infoValue = {
  fontSize: "15px",
  lineHeight: 1.6,
  color: "rgba(255,255,255,0.82)",
};
